import { useCallback, useEffect, useMemo } from 'react';
import type { FundAccountSelection, StatementPeriod } from '../types/dealerFundStatementPeriod.types';
import type { AgreementSummary, FundAccountPostingsPage } from '../types/fundAccountPostings.types';
import { postingDisplayRows, type PostingDisplayRow } from '../utils/postingDisplayRows';
import { postingsReconcile } from '../utils/postingsReconcile';
import type { StatementLine } from '../utils/statementBlocks';
import { buildAgreementSummaryMap, useAgreementSummariesByIds } from './useAgreementSummariesByIds';
import { useFundAccountPostingsQuery } from './useFundAccountPostingsQuery';

export type PostingsLineState = 'loading' | 'error' | 'unverified' | 'not_entitled' | 'mismatch' | 'ok';
export type SummariesState = 'loading' | 'error' | 'ok';

export interface FundAccountPostingsModel {
    state: PostingsLineState;
    page: FundAccountPostingsPage | null;
    displayRows: PostingDisplayRow[];
    summaries: Map<string, AgreementSummary>;
    summariesState: SummariesState;
    onRetry: () => void;
    onReload: () => void;
}

interface Args {
    selection: FundAccountSelection;
    period: StatementPeriod;
    todayIso: string;
    line: StatementLine;
    split: boolean;
    window: { from: string; to: string };
    page: number;
    onPageChange: (page: number) => void;
    onReloadStatement: () => void;
}

const NO_IDS: string[] = [];
const NO_DISPLAY_ROWS: PostingDisplayRow[] = [];

const lineState = (
    query: ReturnType<typeof useFundAccountPostingsQuery>,
    reconciled: boolean,
): PostingsLineState => {
    if (query.isError) return 'error';
    const result = query.data;
    if (!result) return 'loading';
    if (result.status === 'unverified') return 'unverified';
    if (result.status === 'not_entitled') return 'not_entitled';
    return reconciled ? 'ok' : 'mismatch';
};

const summariesStateOf = (query: { isError: boolean; data: unknown }, ids: string[], rowsReady: boolean): SummariesState => {
    if (!rowsReady) return 'loading';
    if (ids.length === 0) return 'ok';
    if (query.isError) return 'error';
    return query.data === undefined ? 'loading' : 'ok';
};

export function useFundAccountPostingsLine({
    selection,
    period,
    todayIso,
    line,
    split,
    window,
    page,
    onPageChange,
    onReloadStatement,
}: Args): FundAccountPostingsModel {
    const query = useFundAccountPostingsQuery(selection, period, todayIso, line.key, page);
    const result = query.data;
    const postingsPage = result?.status === 'ok' ? result.statement : null;
    const reconciled = postingsPage !== null && postingsReconcile(line, window, postingsPage);
    const state = lineState(query, reconciled);
    const clampedPage = state === 'ok' && postingsPage ? Math.max(1, postingsPage.lastPage) : page;

    useEffect(() => {
        if (page > clampedPage) onPageChange(clampedPage);
    }, [page, clampedPage, onPageChange]);

    const agreementIds = useMemo(
        () => (state === 'ok' && postingsPage ? postingsPage.rows.map((row) => row.agreementId) : NO_IDS),
        [state, postingsPage],
    );
    const displayRows = useMemo(
        () => (state === 'ok' && postingsPage ? postingDisplayRows(postingsPage.rows, split) : NO_DISPLAY_ROWS),
        [state, postingsPage, split],
    );
    const summariesQuery = useAgreementSummariesByIds(agreementIds, state === 'ok');
    const summariesData = summariesQuery.isError ? undefined : summariesQuery.data;
    const summaries = useMemo(() => buildAgreementSummaryMap(summariesData), [summariesData]);

    const { refetch } = query;
    const onRetry = useCallback(() => { void refetch({ cancelRefetch: false }); }, [refetch]);
    const onReload = useCallback(() => {
        onReloadStatement();
        void refetch({ cancelRefetch: false });
    }, [onReloadStatement, refetch]);

    return {
        state,
        page: state === 'ok' ? postingsPage : null,
        displayRows,
        summaries,
        summariesState: summariesStateOf(summariesQuery, agreementIds, state === 'ok'),
        onRetry,
        onReload,
    };
}
